import React from "react";
import { styled } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import ListItem from "@material-ui/core/ListItem";
import List from "@material-ui/core/List";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import Slide from "@material-ui/core/Slide";
import { TransitionProps } from "@material-ui/core/transitions";
import { IPlayer, ITeam } from "../../types";
import GroupIcon from "@material-ui/icons/Group";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import SportsIcon from "@material-ui/icons/Sports";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: theme.palette.extra.backgroundGrey,
    borderRadius: 0,
  },
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  background: theme.palette.primary.main,
  padding: theme.spacing(2, 3),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const HeaderContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  flex: 1,
  "& svg": {
    fontSize: "2rem",
    color: "#fff",
  },
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  color: "#fff",
  fontWeight: 600,
  fontSize: "1.25rem",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.1rem",
  },
}));

const CloseButton = styled(IconButton)(() => ({
  color: "#fff",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.1)",
  },
}));

const DialogContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 600,
  fontSize: "0.9rem",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: theme.spacing(2),
  padding: theme.spacing(0, 1),
}));

const TeamsList = styled(List)(() => ({
  padding: 0,
}));

const TeamItem = styled(ListItem)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: 12,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  marginBottom: theme.spacing(1.5),
  padding: theme.spacing(2),
  transition: "border-color 0.2s ease",
  cursor: "pointer",
  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
}));

const TeamItemContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  flex: 1,
}));

const TeamIcon = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: "50%",
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    fontSize: "1.5rem",
    color: "#fff",
  },
}));

const TeamInfo = styled(Box)(({ theme }) => ({
  flex: 1,
  "& .team-name": {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  "& .team-meta": {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    "& svg": {
      fontSize: "1rem",
    },
  },
}));

const ChevronIcon = styled(ChevronRightIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: "1.75rem",
}));

const AssignButton = styled(Button)(({ theme }) => ({
  background: theme.palette.primary.main,
  color: "#fff",
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  fontSize: "1rem",
  textTransform: "none",
  transition: "background 0.2s ease",
  "&:hover": {
    background: theme.palette.primary.dark,
  },
  [theme.breakpoints.down("sm")]: {
    width: "100%",
    padding: theme.spacing(1.25, 2.5),
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(6, 3),
  color: theme.palette.text.secondary,
  "& svg": {
    fontSize: "4rem",
    marginBottom: theme.spacing(2),
    opacity: 0.3,
  },
  "& p": {
    fontSize: "1rem",
  },
}));

interface IAssignTeamModal {
  teams: ITeam[];
  player: IPlayer;
  callback(playerId: string, teamId: string): void;
}

export default function AssignTeamModal({
  player,
  teams,
  callback,
}: IAssignTeamModal) {
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleAssign = (teamId: string) => {
    handleClose();
    callback(player.id, teamId);
  };

  return (
    <div>
      <AssignButton
        variant="contained"
        startIcon={<PersonAddIcon />}
        onClick={handleClickOpen}
      >
        Assign to Team
      </AssignButton>
      <StyledDialog
        fullScreen
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <DialogHeader>
          <HeaderContent>
            <PersonAddIcon />
            <HeaderTitle variant="h6">
              Assign {player.name} {player.surname}
            </HeaderTitle>
          </HeaderContent>
          <CloseButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </CloseButton>
        </DialogHeader>

        <DialogContent>
          <SectionTitle>Select a team</SectionTitle>
          {teams && teams.length > 0 ? (
            <TeamsList>
              {teams.map((team) => (
                <TeamItem
                  key={team.id}
                  button
                  onClick={() => handleAssign(team.id)}
                >
                  <TeamItemContent>
                    <TeamIcon>
                      <GroupIcon />
                    </TeamIcon>
                    <TeamInfo>
                      <div className="team-name">{team.name}</div>
                      <div className="team-meta">
                        <SportsIcon />
                        <span>
                          {team.squad ? `${team.squad.length} players` : "No players"}
                        </span>
                      </div>
                    </TeamInfo>
                  </TeamItemContent>
                  <ChevronIcon />
                </TeamItem>
              ))}
            </TeamsList>
          ) : (
            <EmptyState>
              <GroupIcon />
              <Typography>No teams available</Typography>
              <Typography variant="body2">
                Create a team first to assign players
              </Typography>
            </EmptyState>
          )}
        </DialogContent>
      </StyledDialog>
    </div>
  );
}
